const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_FILE_BYTES = 25 * 1024 * 1024;

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image'));
    };
    img.src = url;
  });

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });

const compressImage = async (file, maxBytes) => {
  const { img, url } = await loadImage(file);
  try {
    let width = img.naturalWidth || img.width || 1;
    let height = img.naturalHeight || img.height || 1;
    const startDim = 1600;
    if (width > startDim || height > startDim) {
      const ratio = Math.min(startDim / width, startDim / height);
      width = Math.max(1, Math.round(width * ratio));
      height = Math.max(1, Math.round(height * ratio));
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let quality = 0.82;
    let blob = null;

    for (let i = 0; i < 16; i += 1) {
      canvas.width = Math.max(width, 1);
      canvas.height = Math.max(height, 1);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      blob = await canvasToBlob(canvas, quality);
      if (blob && blob.size <= maxBytes) break;
      if (quality > 0.4) {
        quality -= 0.1;
      } else {
        width = Math.max(120, Math.round(width * 0.72));
        height = Math.max(120, Math.round(height * 0.72));
        quality = 0.55;
      }
    }

    if (!blob) return file;
    const nextName = (file.name || 'image').replace(/\.[^/.]+$/, '.jpg');
    const compressed = new File([blob], nextName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    return compressed.size < file.size ? compressed : file;
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const prepareUploadFile = async (file, options = {}) => {
  const maxImageBytes = options.maxImageBytes ?? MAX_IMAGE_BYTES;
  if (!file) return null;
  if (!(file instanceof Blob)) {
    return file;
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(
      `File "${file.name || 'upload'}" is too large. Please use a file under 25MB.`
    );
  }
  if (!file.type || !file.type.startsWith('image/')) {
    return file;
  }
  if (file.size <= maxImageBytes) {
    return file;
  }
  return compressImage(file, maxImageBytes);
};

export const getUploadErrorMessage = (error, fallback) => {
  if (error?.message && error.message.includes('too large')) {
    return error.message;
  }
  const status = error?.response?.status;
  if (status === 413 || error?.message?.includes('413')) {
    return 'Upload size too large. Please use an image under 10MB and try again.';
  }
  if (status === 502 || status === 504) {
    return 'Live server is not reachable. Please restart the backend gateway and try again.';
  }
  if (!error?.response) {
    return 'Cannot reach the live API. Please confirm the backend is running and try again.';
  }
  return error?.response?.data?.message || fallback;
};
